import { useRef } from 'react'
import { useHasChangedValues } from './use-has-changed-values'
import { useOnUnmount } from './use-on-unmount'

/**
 * Custom Hook that executes a callback if a dependency changes. It works similar to useEffect,
 * but does not require everything in the callback to be listed as dependency. This solves the
 * issue of stale values in the native useEffect.
 * It is also possible to pass a custom comparison function to perform i.e. deep equality checks.
 * Following the paradigmns in the docs https://react.dev/learn/you-might-not-need-an-effect this hook
 * avoids using useEffect.
 * When to use this instead of useEffect? - Whenever your callback does not rely on react already
 * having updated the dom.
 *
 * @param {() => void|(() => void)} callback - a function that should be executed if any dependency changes. you can return a cleanup-function
 * @param {Array} values - dependency array of values to be checked
 * @param {(a: any, b: any) => boolean} isEqualValueFn - a function that returns true if the value is equal
 *
 * @example
 * const { param1, param2, action } = props
 * const [loading, setLoading] = useState(false)
 * const requestId = useRef(0)
 *
 * useCallbackOnChange(() => {
 *   const currentRequest = requestId.current
 *   // in a useEffect this would trigger an instant additional render cycle
 *   setLoading(true)
 *   action(param1, param2)
 *     .then(() => {
 *       currentRequest === requestId.current && setLoading(false)
 *     })
 *   // cleanup function, executed before the next callback is triggered and on unmount
 *   return () => requestId.current++
 * }, [param1])
 */
export function useCallbackOnChange(callback, dependencies, isEqualValueFn) {
    const hasChanges = useHasChangedValues(dependencies, isEqualValueFn)
    const cleanupFunction = useRef()
    if (hasChanges) {
        if (typeof cleanupFunction.current === 'function') {
            cleanupFunction.current()
        }
        cleanupFunction.current = callback()
    }
    useOnUnmount(() => {
        if (typeof cleanupFunction.current === 'function') {
            cleanupFunction.current()
        }
    })
}
